import { Button } from '@/components/button';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { DataTable } from '@/components/table/datatable';
import { idrFormatter } from '@/lib/utils/currency';
import { clearErrors } from '@/reducers/ots';
import { AppDispatch } from '@/root/store';
import { ShowEvent } from '@/services/events';
import { GetVouchers, StoreVoucher } from '@/services/vouchers';
import { NoSymbolIcon, PlusIcon } from '@heroicons/react/24/outline';
import { DocumentCheckIcon, EyeIcon } from '@heroicons/react/24/solid';
import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

interface Voucher {
  id: number;
  code: string;
  name: string;
  visibility: string;
  type: 'percentage' | 'fixed';
  valid_from: Date;
  valid_until: Date;
  amount: number;
  used: number;
  remain: number;
  used_cost: number;
}

const columns: ColumnDef<Voucher>[] = [
  {
    accessorKey: 'code',
    header: 'Kode voucher',
  },
  {
    accessorKey: 'name',
    header: 'Nama voucher',
  },
  {
    accessorKey: 'visibility',
    header: 'Visibilitas',
  },
  {
    accessorFn: (row) =>
      row.type === 'percentage'
        ? 'Persentase'
        : row.type === 'fixed'
          ? 'Nominal'
          : '?',
    header: 'Tipe voucher',
  },
  {
    accessorFn: (row) => {
      return `${moment(row.valid_from).format('lll')} - ${moment(row.valid_until).format('lll')}`;
    },
    header: 'Masa berlaku',
  },
  {
    accessorKey: 'used',
    header: 'Voucher terpakai',
  },
  {
    accessorKey: 'remain',
    header: 'Voucher tersisa',
  },
  {
    header: 'Jumlah potongan',
    cell: ({ row }) => {
      const cost = row.original.amount;
      const type = row.original.type;
      return (
        <div>
          {type === 'fixed' && (
            <span className='text-danger'>{idrFormatter.format(cost)}</span>
          )}
          {type === 'percentage' && (
            <span className='text-danger'>{cost + '%'}</span>
          )}
        </div>
      );
    },
  },
  {
    header: 'Biaya voucher terpakai',
    cell: ({ row }) => {
      const usedCost = row.original.used_cost;
      return (
        <span className='text-danger'>{idrFormatter.format(usedCost)}</span>
      );
    },
  },
  {
    header: 'Aksi',
    cell: ({ row }) => {
      return (
        <Button
          element={'link'}
          to={row.original.code}
          variant='neutral'
          size='sm'
          leftIcon={<EyeIcon />}
        >
          Detail
        </Button>
      );
    },
  },
];

export const defaultVoucher = {
  code: '',
  name: '',
  type: '',
  visibility: '',
  amount: 0,
  valid_from: '',
  valid_until: '',
  quantity: 0,
  status: 1,
};

export function OrgEventDetailVouchers() {
  const location = useLocation();
  const { slug } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [voucherModal, setVoucherModal] = useState(false);
  const [totalCostUsed, setTotalCostUsed] = useState(0);
  const [formData, setFormData] = useState(defaultVoucher);

  const { vouchers, voucher, errors, paginator } = useSelector(
    (state: any) => state.vouchers
  );
  const { event } = useSelector((state: any) => state.event);

  useEffect(() => {
    const totalCostUsed = vouchers?.reduce((prev: any, current: any) => {
      return prev + current.used_cost;
    }, 0);

    setTotalCostUsed(totalCostUsed);
  }, [vouchers]);

  const setData = (value: string, element: string) => {
    setFormData({ ...formData, [element]: value });

    dispatch(clearErrors());
  };

  const ctaButton = (
    <Button onClick={() => setVoucherModal(true)} leftIcon={<PlusIcon />}>
      Buat Voucher
    </Button>
  );

  const queryParams = new URLSearchParams(location.search);
  const searchQuery: string = queryParams.get('search') ?? '';

  const submit = () => {
    dispatch(StoreVoucher(event.id, formData));
  };

  useEffect(() => {
    dispatch(GetVouchers(slug, searchQuery, page));

    setFormData(defaultVoucher);
    setVoucherModal(false);
  }, [slug, searchQuery, page, voucher]);

  useEffect(() => {
    dispatch(ShowEvent(slug));
  }, [slug]);

  return (
    <>
      <Modal
        show={voucherModal}
        onRequestClose={() => setVoucherModal(false)}
        width='medium'
        title='Buat voucher baru'
        body={
          <div className='flex flex-col gap-4'>
            <div className='grid grid-cols-2 gap-3'>
              <FormLabel label='Kode voucher' errorText={errors.code}>
                <Input
                  type='text'
                  maxLength={6}
                  value={formData.code}
                  onChange={(e) =>
                    setData(e.target.value?.toUpperCase(), 'code')
                  }
                />
              </FormLabel>
              <FormLabel label='Nama voucher' errorText={errors.name}>
                <Input
                  type='text'
                  onChange={(e) => setData(e.target.value, 'name')}
                />
              </FormLabel>
              {/*<FormLabel label='Minimal pembelian'>
                <Input type='text' leftDeco='Rp' />
              </FormLabel>*/}
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <FormLabel label='Jumlah voucher' errorText={errors.quantity}>
                <Input
                  type='number'
                  onChange={(e) => setData(e.target.value, 'quantity')}
                  required
                />
              </FormLabel>
              <FormLabel label='Tipe voucher' errorText={errors.type}>
                <Select
                  onValueChange={(e) => {
                    setData(e, 'visibility');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue defaultValue='public' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={'public'}>Public</SelectItem>
                      <SelectItem value={'private'}>Private</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormLabel>
            </div>
            {/* TODO: datepicker here */}
            <div className='grid grid-cols-2 gap-3'>
              <FormLabel
                label='Voucher mulai valid'
                errorText={errors.valid_from}
              >
                <Input
                  type='datetime-local'
                  onChange={(e) => setData(e.target.value, 'valid_from')}
                />
              </FormLabel>
              <FormLabel
                label='Voucher berakhir'
                errorText={errors.valid_until}
              >
                <Input
                  type='datetime-local'
                  onChange={(e) => setData(e.target.value, 'valid_until')}
                />
              </FormLabel>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <FormLabel label='Tipe potongan' errorText={errors.type}>
                <Select
                  onValueChange={(e) => {
                    setData(e, 'type');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue defaultValue='fixed' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={'percentage'}>Persentase</SelectItem>
                      <SelectItem value={'fixed'}>Nominal</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormLabel>
              <FormLabel label='Jumlah potongan' errorText={errors.amount}>
                <Input
                  type='text'
                  leftDeco={formData.type === 'fixed' && 'Rp'}
                  rightDeco={formData.type === 'percentage' && '%'}
                  onChange={(e) => setData(e.target.value, 'amount')}
                />
              </FormLabel>
            </div>
            {/*<FormLabel label='Syarat & Ketentuan'>
              <Textarea />
            </FormLabel>*/}
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setVoucherModal(false)}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button onClick={submit} leftIcon={<DocumentCheckIcon />}>
              Simpan
            </Button>
          </div>
        }
      />
      <DataTable
        name='Voucher event'
        noun='voucher'
        ctaButton={ctaButton}
        columns={columns}
        data={vouchers}
        total={paginator.to}
        currentTotal={paginator.total}
        currentPage={paginator.current_page}
        pages={paginator.last_page}
        onPageRequest={(nextPage) => setPage(nextPage)}
        additionalRows={
          <>
            <tr className='[&>td]:bg-primary-surface'>
              <td></td>
              <td className='text-sm'>Total Pendapatan Terpotong</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td className='text-sm text-danger'>
                {idrFormatter.format(totalCostUsed)}
              </td>
              <td></td>
            </tr>
          </>
        }
      />
    </>
  );
}
